/**
 * TrashBin — recoverable recycle bin for every soft-deletable item
 * (drafts, blog posts, studio documents, ideas).
 *
 *   /portal/workspace/trash
 *
 * Each row shows kind + title + summary + when it was deleted, with
 * Restore + Permanent-delete actions. "Empty trash" button at the top.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Trash2, RotateCcw, X, AlertTriangle } from "lucide-react";
import { useAdminToken, studioFetch } from "../components/studio/_shared";
import ContentPlannerLayout from "../components/layout/ContentPlannerLayout";

const KIND_LABELS = {
  draft:    { l: "Planner draft",    c: "#0E7A82" },
  post:     { l: "Blog post",        c: "#3F80BF" },
  document: { l: "PDF document",     c: "#8A6914" },
  idea:     { l: "Idea",             c: "#B85C38" },
};

export default function TrashBin() {
  const navigate = useNavigate();
  const token = useAdminToken();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!token) navigate("/admin/login", { replace: true });
  }, [token, navigate]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError("");
    try {
      const path = filter ? `/api/workspace/trash?kind=${filter}` : "/api/workspace/trash";
      const d = await studioFetch(path, { token });
      setItems(d.items || []);
    } catch (e) {
      if (/401|403/.test(e.message)) {
        localStorage.removeItem("hv_admin_token");
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(e.message);
    } finally { setLoading(false); }
  }, [token, filter, navigate]);

  useEffect(() => { load(); }, [load]);

  const restore = async (it) => {
    setBusy(it.id);
    try {
      await studioFetch(`/api/workspace/trash/${it.kind}/${it.id}/restore`, {
        method: "POST", token,
      });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  };

  const purge = async (it) => {
    if (!window.confirm(`Permanently delete this ${it.kind}? This cannot be undone.`)) return;
    setBusy(it.id);
    try {
      await studioFetch(`/api/workspace/trash/${it.kind}/${it.id}`, {
        method: "DELETE", token,
      });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  };

  const emptyTrash = async () => {
    if (!window.confirm(
      "Permanently delete EVERY item in the trash" +
      (filter ? ` (only ${filter}s)` : "") +
      "? This cannot be undone.",
    )) return;
    setBusy("__empty__");
    try {
      const path = filter ? `/api/workspace/trash/empty?kind=${filter}` : "/api/workspace/trash/empty";
      await studioFetch(path, { method: "POST", token });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  };

  return (
    <ContentPlannerLayout>
      <Helmet>
        <title>Trash — HidroVerse Content Manager</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div data-testid="trash-bin">

        {/* Filter strip + Empty */}
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono uppercase"
                  style={{ fontSize: "0.6rem", letterSpacing: "0.20em",
                           color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
              Show:
            </span>
            <button type="button" onClick={() => setFilter("")}
                    data-testid="trash-filter-all"
                    className="hv-pill"
                    style={!filter ? { background: "#0C2A40", color: "#FFFFFF" } : undefined}>
              Everything
            </button>
            {Object.entries(KIND_LABELS).map(([k, v]) => (
              <button key={k} type="button" onClick={() => setFilter(k)}
                      data-testid={`trash-filter-${k}`}
                      className="hv-pill"
                      style={filter === k ? { background: "#0C2A40", color: "#FFFFFF" } : undefined}>
                {v.l}
              </button>
            ))}
          </div>
          {items.length > 0 && (
            <button type="button" onClick={emptyTrash}
                    disabled={busy === "__empty__"}
                    data-testid="trash-empty"
                    className="hv-pill is-danger">
              <AlertTriangle size={11} />
              {busy === "__empty__"
                ? "Emptying…"
                : filter ? `Empty (${filter}s)` : "Empty trash"}
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 px-4 py-3"
               style={{ background: "rgba(184,92,56,0.10)",
                        border: "1px solid rgba(184,92,56,0.35)",
                        color: "#B85C38", fontSize: "0.85rem",
                        borderRadius: 10 }}
               data-testid="trash-error">{error}</div>
        )}

        {loading && (
          <div className="font-mono uppercase"
               style={{ fontSize: "0.65rem", letterSpacing: "0.22em",
                        color: "rgba(12,42,64,0.55)" }}>Loading…</div>
        )}

        {!loading && items.length === 0 && (
          <div className="hv-card-soft p-8 text-center" data-testid="trash-empty-state">
            <Trash2 size={20} style={{ color: "#1FB6C1", margin: "0 auto" }} />
            <p className="mt-3 font-light"
               style={{ fontSize: "0.88rem", color: "rgba(12,42,64,0.65)" }}>
              Trash is empty.
            </p>
          </div>
        )}

        <ul className="space-y-3" data-testid="trash-list">
          {items.map((it) => {
            const kind = KIND_LABELS[it.kind] || { l: it.kind, c: "#0C2A40" };
            return (
              <li key={`${it.kind}-${it.id}`}
                  className="hv-card-soft flex items-start gap-3 flex-wrap"
                  style={{ padding: 14 }}
                  data-testid={`trash-item-${it.kind}-${it.id}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="px-2 py-0.5 font-mono uppercase"
                          style={{ background: `${kind.c}20`,
                                   color: kind.c,
                                   fontSize: "0.58rem", letterSpacing: "0.18em",
                                   fontWeight: 700, borderRadius: 3 }}>
                      {kind.l}
                    </span>
                    <span className="font-mono"
                          style={{ fontSize: "0.6rem", color: "rgba(12,42,64,0.55)" }}>
                      deleted {new Date(it.deleted_at).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ fontSize: "0.95rem", fontWeight: 600,
                                lineHeight: 1.3 }}>
                    {it.title}
                  </div>
                  {it.summary && (
                    <div className="font-light mt-1"
                         style={{ fontSize: "0.82rem",
                                  color: "rgba(12,42,64,0.65)",
                                  lineHeight: 1.4 }}>
                      {it.summary}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => restore(it)}
                          disabled={busy === it.id}
                          data-testid={`trash-restore-${it.kind}-${it.id}`}
                          className="hv-pill is-active">
                    <RotateCcw size={11} /> Restore
                  </button>
                  <button type="button" onClick={() => purge(it)}
                          disabled={busy === it.id}
                          data-testid={`trash-purge-${it.kind}-${it.id}`}
                          className="hv-pill is-danger">
                    <X size={11} /> Permanently delete
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </ContentPlannerLayout>
  );
}
